import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { GroupItem } from "common/types/graphql";
import { useViewer } from "common/hooks/data/useViewer";
import { useViewerSubscriptions } from "common/hooks/data/useViewerSubscriptions";
import { useRegionCode } from "modules/i18n/hooks/useRegionCode";
import { useNotifications } from "common/components/Notification";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { isErrorResponseFormat, useToggleSubscription } from "modules/newsletters/hooks/useToggleSubscription";
import { useAnonymousNewslettersContext, useSubscribedNewslettersContext } from "modules/newsletters/contexts";
import { NewsletterCard } from "../NewsletterCard";
import { trackNewsletterSubCardDeselected, trackNewsletterSubCardSelected } from "common/lib/tcEventCollector";
const NOTIFICATION_TIMEOUT = 5000; // In milliseconds

export interface NewsletterSubscriberProps {
  newsletter: GroupItem;
  rank: number;
}

/**
 * Container component for newsletter subscription logic in v2 design.
 * Handles both signed-in and anonymous user subscription flows.
 *
 * => Anonymous users context: we will only toggle selected newsletters in the context until they submit their email.
 * => Anonymous users after submission: we prevent unsubscription but allow additional subscriptions with ReCAPTCHA.
 * => Signed-in users context: we will toggle selected newsletters in the context and make a request to the backend to
 * subscribe/unsubscribe the user to the selected newsletters.
 */
export const NewsletterSubscriber = t0 => {
  "use memo";

  const $ = _c(29);
  const {
    newsletter,
    rank
  } = t0;
  const regionCode = useRegionCode();
  const {
    ids: newsletterIds,
    toggle: toggleContext
  } = useSubscribedNewslettersContext();
  const {
    set: setNotifications
  } = useNotifications();
  const {
    isSignedIn
  } = useViewer();
  const {
    getPrimaryEmailAddress
  } = useViewerSubscriptions();
  const {
    email: anonymousEmail,
    isSubscribed,
    recaptchaRef
  } = useAnonymousNewslettersContext();
  const {
    toggleSubscription,
    isLoading
  } = useToggleSubscription();
  const t = useTranslations("newsletters");
  let t1;
  if ($[0] !== newsletter.id || $[1] !== newsletterIds) {
    t1 = newsletterIds.has(newsletter.id);
    $[0] = newsletter.id;
    $[1] = newsletterIds;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isSelected = t1;
  let t2;
  if ($[3] !== setNotifications || $[4] !== t || $[5] !== toggleContext) {
    t2 = (error, newsletterId) => {
      const [firstError] = isErrorResponseFormat(error) ? error.errors : [];
      setNotifications([{
        type: "error",
        title: firstError || t("v2.notification.error")
      }]);
      toggleContext(newsletterId);
    };
    $[3] = setNotifications;
    $[4] = t;
    $[5] = toggleContext;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleSubscriptionError = t2;
  let t3;
  if ($[7] !== anonymousEmail || $[8] !== getPrimaryEmailAddress || $[9] !== handleSubscriptionError || $[10] !== isLoading || $[11] !== isSignedIn || $[12] !== isSubscribed || $[13] !== newsletter || $[14] !== newsletterIds || $[15] !== rank || $[16] !== recaptchaRef || $[17] !== regionCode || $[18] !== setNotifications || $[19] !== t || $[20] !== toggleContext || $[21] !== toggleSubscription) {
    t3 = () => {
      if (isLoading) {
        return;
      }
      const {
        id
      } = newsletter;
      const isCurrentlySubscribed = newsletterIds.has(id);
      const newsletterListIds = new Set([id]);
      if (!isSignedIn) {
        if (isSubscribed && isCurrentlySubscribed) {
          window.alert(t("v2.anonymous_unsubscribe"));
          return;
        }
        toggleContext(id);
        if (isSubscribed) {
          (async () => {
            const recaptchaToken = (await recaptchaRef.current?.executeAsync()) ?? undefined;
            ;
            try {
              await toggleSubscription({
                regionCode,
                email: anonymousEmail,
                action: "subscribe",
                newsletterListIds,
                recaptchaToken
              });
            } catch (t4) {
              const error_0 = t4;
              handleSubscriptionError(error_0, id);
            }
          })();
        }
        if (isCurrentlySubscribed) {
          trackNewsletterSubCardDeselected({
            newsletterId: id,
            rank,
            userLoggedIn: false
          });
        } else {
          trackNewsletterSubCardSelected({
            newsletterId: id,
            rank,
            userLoggedIn: false
          });
        }
        return;
      }
      const email = getPrimaryEmailAddress();
      const action = isCurrentlySubscribed ? "unsubscribe" : "subscribe";
      const message = action === "subscribe" ? t("v2.notification.subscribed_to", {
        newsletterName: newsletter.name
      }) : t("v2.notification.unsubscribed_from", {
        newsletterName: newsletter.name
      });
      toggleContext(id);
      if (isCurrentlySubscribed) {
        trackNewsletterSubCardDeselected({
          newsletterId: id,
          rank,
          userLoggedIn: true
        });
      } else {
        trackNewsletterSubCardSelected({
          newsletterId: id,
          rank,
          userLoggedIn: true
        });
      }
      (async () => {
        ;
        try {
          await toggleSubscription({
            regionCode,
            email,
            action,
            newsletterListIds
          });
          setNotifications({
            type: "success",
            title: message,
            duration: NOTIFICATION_TIMEOUT
          });
        } catch (t5) {
          const error_1 = t5;
          handleSubscriptionError(error_1, id);
        }
      })();
    };
    $[7] = anonymousEmail;
    $[8] = getPrimaryEmailAddress;
    $[9] = handleSubscriptionError;
    $[10] = isLoading;
    $[11] = isSignedIn;
    $[12] = isSubscribed;
    $[13] = newsletter;
    $[14] = newsletterIds;
    $[15] = rank;
    $[16] = recaptchaRef;
    $[17] = regionCode;
    $[18] = setNotifications;
    $[19] = t;
    $[20] = toggleContext;
    $[21] = toggleSubscription;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const handleToggle = t3;
  let t4;
  if ($[23] !== handleToggle || $[24] !== isLoading || $[25] !== isSelected || $[26] !== isSubscribed || $[27] !== newsletter) {
    t4 = <NewsletterCard newsletter={newsletter} selected={isSelected} loading={isLoading} isSubscribed={isSubscribed} onToggle={handleToggle} />;
    $[23] = handleToggle;
    $[24] = isLoading;
    $[25] = isSelected;
    $[26] = isSubscribed;
    $[27] = newsletter;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
};